import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import Layout from '../components/Layout';
import { productsAPI, categoriesAPI } from '../services/api';
import api from '../services/api';
import { useToast } from '../components/Toast';
import {
  Search, CheckCircle, XCircle, RefreshCw, Package,
  X, Eye, Edit3, ChevronLeft, ChevronRight, Truck,
  Percent, Save, AlertCircle, Layers
} from 'lucide-react';

// Detect mobile viewport
const isMobile = () => window.innerWidth < 1024;

const API_BASE  = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
const HOST_BASE = API_BASE.replace(/\/api\/?$/, '');

// ── Image utilities ───────────────────────────────────────────────────────────
function extractFilename(s) {
  const clean = s.split('?')[0].split('#')[0];
  const parts = clean.split('/');
  return parts[parts.length - 1] || '';
}

function buildCandidates(raw) {
  if (!raw) return [];
  const s = String(raw).trim();
  if (!s) return [];
  const filename = extractFilename(s);
  const seen = new Set();
  const candidates = [];

  const add = (url) => {
    if (url && !seen.has(url)) { seen.add(url); candidates.push(url); }
  };

  if (s.startsWith('http://') || s.startsWith('https://') || s.startsWith('data:')) {
    // Full URL (S3 / CDN / base64) – use as-is
    add(s);
  } else if (s.startsWith('/api/')) {
    add(HOST_BASE + s);
  } else if (s.startsWith('/')) {
    // Server-relative path such as "/uploads/x.jpg": it lives on the BACKEND,
    // not on the admin site, so try the backend host first.
    add(HOST_BASE + s);
    add(API_BASE.replace(/\/$/, '') + s);
    add(s);
  } else {
    // Bare filename or "uploads/x.jpg" without a leading slash
    const rel = s.replace(/^\.?\//, '');
    add(rel.startsWith('uploads/') ? `${HOST_BASE}/${rel}` : `${HOST_BASE}/uploads/${rel}`);
    add(`${API_BASE.replace(/\/$/, '')}/uploads/${filename}`);
  }
  return candidates;
}

function parseImages(imagesField) {
  if (!imagesField) return [];
  if (Array.isArray(imagesField)) return imagesField.filter(Boolean);
  const s = String(imagesField).trim();
  if (!s) return [];
  try {
    let parsed = JSON.parse(s);
    // If backend double-encodes JSON (i.e. parsed is a JSON string), try parsing again
    if (typeof parsed === 'string') {
      const inner = parsed.trim();
      try {
        const parsed2 = JSON.parse(inner);
        if (Array.isArray(parsed2)) return parsed2.filter(Boolean);
        if (typeof parsed2 === 'string' && parsed2.trim()) return [parsed2.trim()];
      } catch {
        // fallthrough to return the string itself
      }
      if (parsed.trim()) return [parsed.trim()];
      return [];
    }
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    if (typeof parsed === 'string' && parsed.trim()) return [parsed.trim()];
    return [];
  } catch {
    if (s.includes(',')) return s.split(',').map(x => x.trim()).filter(Boolean);
    return [s];
  }
}

// ── FallbackImage ─────────────────────────────────────────────────────────────
function FallbackImage({ raw, alt, style, className }) {
  const candidates = buildCandidates(raw);
  const [idx,    setIdx]    = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => { setIdx(0); setFailed(false); }, [raw]);

  const handleError = () => {
    if (idx + 1 < candidates.length) {
      setIdx(i => i + 1);
    } else {
      setFailed(true);
    }
  };

  if (!failed && candidates[idx]) {
    return (
      <img
        src={candidates[idx]}
        alt={alt}
        style={{ ...style, display: 'block' }}
        className={className}
        loading="lazy"
        decoding="async"
        onError={handleError}
      />
    );
  }

  const fontSize = style?.width > 60 ? 40 : 20;
  return (
    <div style={{
      width: '100%', height: '100%',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f0f9f0', fontSize,
    }}>
      🍱
    </div>
  );
}

FallbackImage.propTypes = {
  raw: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.shape({}),
  className: PropTypes.string,
};

FallbackImage.defaultProps = {
  raw: null,
  alt: '',
  style: {},
  className: undefined,
};

// ── ImageSlider ───────────────────────────────────────────────────────────────
const CARD_IMG_HEIGHT = 160;

function ImageSlider({ images, height = CARD_IMG_HEIGHT, borderRadius = 8 }) {
  const [current, setCurrent] = useState(0);
  const total = images.length;

  if (total === 0) {
    return (
      <div style={{ height, borderRadius, background: '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 40 }}>🍱</span>
      </div>
    );
  }

  if (total === 1) {
    return (
      <div style={{ height, borderRadius, overflow: 'hidden', background: '#f0f9f0', position: 'relative', width: '100%' }}>
        <FallbackImage raw={images[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    );
  }

  const prev = (e) => { e.stopPropagation(); setCurrent(c => (c - 1 + total) % total); };
  const next = (e) => { e.stopPropagation(); setCurrent(c => (c + 1) % total); };

  return (
    <div style={{ position: 'relative', height, borderRadius, overflow: 'hidden', background: '#f0f9f0', width: '100%', flexShrink: 0 }}>
      <FallbackImage raw={images[current]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      <SliderButton direction="left"  onClick={prev}><ChevronLeft  size={13} /></SliderButton>
      <SliderButton direction="right" onClick={next}><ChevronRight size={13} /></SliderButton>
      <div style={{ position: 'absolute', bottom: 5, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 4 }}>
        {images.map((img, i) => (
          <div
            key={img || i}
            onClick={e => { e.stopPropagation(); setCurrent(i); }}
            style={{ width: i === current ? 14 : 6, height: 6, borderRadius: 3, background: i === current ? '#fff' : 'rgba(255,255,255,0.55)', cursor: 'pointer', transition: 'all 0.2s' }}
          />
        ))}
      </div>
      <span style={{ position: 'absolute', top: 5, right: 6, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 10, borderRadius: 8, padding: '1px 6px' }}>
        {current + 1}/{total}
      </span>
    </div>
  );
}

ImageSlider.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  height: PropTypes.number,
  borderRadius: PropTypes.number,
};

ImageSlider.defaultProps = {
  height: CARD_IMG_HEIGHT,
  borderRadius: 8,
};

// ── Extracted: slider nav button ──────────────────────────────────────────────
function SliderButton({ direction, onClick, children }) {
  const pos = direction === 'left' ? { left: 4 } : { right: 4 };
  return (
    <button
      onClick={onClick}
      style={{ position: 'absolute', ...pos, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', padding: 0 }}
    >
      {children}
    </button>
  );
}

SliderButton.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

// ── ModalImageSlider ──────────────────────────────────────────────────────────
function ModalImageSlider({ images, size = 120 }) {
  const [active, setActive] = useState(0);
  const total = images.length;

  if (total === 0) {
    return (
      <div style={{ width: size, height: size, borderRadius: 10, background: '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 40 }}>🍱</span>
      </div>
    );
  }

  const prev = () => setActive(c => (c - 1 + total) % total);
  const next = () => setActive(c => (c + 1) % total);

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: size, height: size, borderRadius: 10, overflow: 'hidden', background: '#f0f9f0', flexShrink: 0, position: 'relative', border: '1px solid var(--border)' }}>
        <FallbackImage raw={images[active]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        {total > 1 && (
          <>
            <ModalSliderButton direction="left"  onClick={prev}><ChevronLeft  size={12} /></ModalSliderButton>
            <ModalSliderButton direction="right" onClick={next}><ChevronRight size={12} /></ModalSliderButton>
          </>
        )}
      </div>
      {total > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {images.slice(0, 5).map((img, i) => (
            <div
              key={`thumb-${img || i}`}
              onClick={() => setActive(i)}
              style={{ width: 36, height: 36, borderRadius: 6, overflow: 'hidden', cursor: 'pointer', border: `2px solid ${i === active ? 'var(--primary)' : 'var(--border)'}`, background: '#f0f9f0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
            >
              <FallbackImage raw={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

ModalImageSlider.propTypes = {
  images: PropTypes.arrayOf(PropTypes.string).isRequired,
  size: PropTypes.number,
};

ModalImageSlider.defaultProps = {
  size: 120,
};

function ModalSliderButton({ direction, onClick, children }) {
  const pos = direction === 'left' ? { left: 2 } : { right: 2 };
  return (
    <button
      onClick={onClick}
      style={{ position: 'absolute', ...pos, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.45)', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', padding: 0 }}
    >
      {children}
    </button>
  );
}

ModalSliderButton.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

// ── PriceDisplay ──────────────────────────────────────────────────────────────
function PriceDisplay({ sellerPrice, adminCharges, discount, deliveryCharges, compact = false }) {
  const base        = Number(sellerPrice    || 0);
  const charges     = Number(adminCharges   || 0);
  const disc        = Number(discount       || 0);
  const delivery    = Number(deliveryCharges || 0);
  const beforeDiscount = base + charges;
  const customerPrice  = Math.max(0, beforeDiscount - disc);
  const hasDiscount    = disc > 0;
  const hasDelivery    = delivery > 0;
  const discPct        = hasDiscount && beforeDiscount > 0 ? Math.round((disc / beforeDiscount) * 100) : 0;

  if (compact) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {hasDiscount && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', textDecoration: 'line-through' }}>
              ₹{beforeDiscount.toLocaleString('en-IN')}
            </span>
          )}
          <span style={{ fontSize: 16, fontWeight: 800, color: hasDiscount ? 'var(--green)' : 'var(--text-primary)' }}>
            ₹{customerPrice.toLocaleString('en-IN')}
          </span>
          {hasDiscount && (
            <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--red)', borderRadius: 4, padding: '1px 5px' }}>
              -{discPct}%
            </span>
          )}
        </div>
        {charges > 0 && (
          <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'block' }}>
            incl. ₹{charges.toLocaleString('en-IN')} platform fee
          </span>
        )}
        {hasDelivery && (
          <span style={{ fontSize: 10, color: '#2563eb', display: 'block' }}>
            + ₹{delivery.toLocaleString('en-IN')} delivery
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {hasDiscount && (
          <span style={{ fontSize: 15, color: 'var(--text-muted)', textDecoration: 'line-through' }}>
            ₹{beforeDiscount.toLocaleString('en-IN')}
          </span>
        )}
        <span style={{ fontSize: 20, fontWeight: 800, color: hasDiscount ? 'var(--green)' : 'inherit' }}>
          ₹{customerPrice.toLocaleString('en-IN')}
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)', marginLeft: 4 }}>customer pays</span>
        </span>
        {hasDiscount && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#ef4444', borderRadius: 6, padding: '2px 7px' }}>
            {discPct}% OFF
          </span>
        )}
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        Seller ₹{base.toLocaleString('en-IN')}
        {charges > 0 && <> + Platform ₹{charges.toLocaleString('en-IN')}</>}
        {hasDiscount && <> − Discount ₹{disc.toLocaleString('en-IN')}</>}
      </span>
      {hasDelivery && (
        <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 500 }}>
          🚚 Delivery ₹{delivery.toLocaleString('en-IN')} (shown to customer)
        </span>
      )}
    </div>
  );
}

PriceDisplay.propTypes = {
  sellerPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  adminCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  deliveryCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  compact: PropTypes.bool,
};

PriceDisplay.defaultProps = {
  sellerPrice: 0,
  adminCharges: 0,
  discount: 0,
  deliveryCharges: 0,
  compact: false,
};

// ── RejectModal ───────────────────────────────────────────────────────────────
function RejectModal({ product, onConfirm, onClose, loading }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Reject Product</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Rejecting: <strong style={{ color: 'var(--text-primary)' }}>{product?.name}</strong>
        </p>
        <div className="form-group">
          <label>Reason for rejection</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. Image quality is poor, missing description..." />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(reason)} disabled={loading || !reason.trim()}>
            {loading ? 'Rejecting...' : 'Reject Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

RejectModal.propTypes = {
  product: PropTypes.shape({ name: PropTypes.string }),
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

RejectModal.defaultProps = {
  product: null,
};

// ── ApproveModal ──────────────────────────────────────────────────────────────
function ApproveModal({ product, onConfirm, onClose, loading }) {
  const [remark,  setRemark]  = useState('');
  const [charges, setCharges] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">Approve Product</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Approving: <strong style={{ color: 'var(--text-primary)' }}>{product?.name}</strong>
        </p>
        <div className="form-group">
          <label>Admin Charges (₹) <span style={{ color: 'var(--red)' }}>*</span></label>
          <input type="number" min="0" step="0.01" value={charges} onChange={e => setCharges(e.target.value)} placeholder="e.g. 10, 25, 50" />
          <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>This amount will be shown to customers as platform fee</small>
        </div>
        <div className="form-group">
          <label>Admin Remark (optional)</label>
          <textarea value={remark} onChange={e => setRemark(e.target.value)} rows={2} placeholder="e.g. Good quality product..." />
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={() => onConfirm(remark, charges)} disabled={loading || !charges.trim()}>
            {loading ? 'Approving...' : 'Approve Product'}
          </button>
        </div>
      </div>
    </div>
  );
}

ApproveModal.propTypes = {
  product: PropTypes.shape({ name: PropTypes.string }),
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};

ApproveModal.defaultProps = {
  product: null,
};

// ── BulkPlatformFeeModal ──────────────────────────────────────────────────────
function BulkPlatformFeeModal({ onClose, onApplied }) {
  const [fee,      setFee]      = useState('');
  const [applying, setApplying] = useState(false);
  const [done,     setDone]     = useState(null);
  const toast = useToast();

  const applyViaFallback = async (amount) => {
    const listRes  = await api.get('/admin/products', { params: { page: 0, size: 1000 } });
    const items    = listRes.data.content || [];
    let success = 0;
    let failed  = 0;
    await Promise.allSettled(
      items.map(p =>
        api.put(`/admin/products/${p.id || p.productId}`, { adminCharges: amount })
          .then(() => { success += 1; })
          .catch(() => { failed += 1; })
      )
    );
    return { success, failed };
  };

  const handleApply = async () => {
    const amount = Number(fee);
    if (isNaN(amount) || amount < 0) { toast('Please enter a valid amount', 'error'); return; }
    setApplying(true);
    try {
      const res = await api.post('/admin/products/bulk-platform-fee', { adminCharges: amount });
      const { updatedCount = 0, failedCount = 0 } = res.data || {};
      setDone({ success: updatedCount, failed: failedCount });
      toast(`Platform fee ₹${amount} applied — ${updatedCount} products updated`, 'success');
      onApplied();
    } catch {
      try {
        const result = await applyViaFallback(amount);
        setDone(result);
        toast(`Platform fee ₹${amount} — ${result.success} products updated${result.failed ? `, ${result.failed} failed` : ''}`, 'success');
        onApplied();
      } catch {
        toast('Bulk update failed', 'error');
      }
    } finally {
      setApplying(false);
    }
  };

  if (done) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: 420 }}>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Platform fee applied successfully!</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              <strong style={{ color: '#16a34a' }}>{done.success}</strong> products updated
              {done.failed > 0 && <>, <strong style={{ color: '#ef4444' }}>{done.failed}</strong> failed</>}
            </div>
          </div>
          <div className="modal-footer" style={{ justifyContent: 'center' }}>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Layers size={16} color="#16a34a" /> Bulk Platform Fee
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#15803d' }}>
          ⚠️ This will override the platform fee for <strong>all products</strong>.
        </div>
        <div className="form-group">
          <label>Platform Fee Amount (₹)</label>
          <input type="number" min="0" step="0.01" value={fee} onChange={e => setFee(e.target.value)} placeholder="e.g. 10, 25, 50" autoFocus />
          <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>Setting 0 will remove the platform fee from all products.</small>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={handleApply} disabled={applying || fee === ''} style={{ gap: 6 }}>
            <Layers size={14} />{applying ? 'Applying…' : 'Apply to All Products'}
          </button>
        </div>
      </div>
    </div>
  );
}

BulkPlatformFeeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onApplied: PropTypes.func.isRequired,
};

// ── ServiceChargesModal ───────────────────────────────────────────────────────
// Admin manages the customer-facing order charges (service_charges table):
// platform_fee, delivery_fee, delivery_fee_multi_item, rain_fee + free-delivery threshold
// + delivery fee SLABS by number of items in the cart (delivery_fee_slabs table).
//
// Delivery fee logic on the backend (ServiceChargeService.calculate):
//   1. If an active slab matches the cart's distinct item count -> slab fee is used.
//   2. Otherwise -> delivery_fee (1 item) / delivery_fee_multi_item (2+ items).
//   3. Free-delivery-above threshold overrides both (fee becomes ₹0).
const CHARGE_ORDER = ['platform_fee', 'delivery_fee', 'rain_fee'];
const CHARGE_ICONS = { platform_fee: '💰', delivery_fee: '🚚', rain_fee: '🌧️' };

// ── Slab helpers ──────────────────────────────────────────────────────────────
let slabUidCounter = 0;
const newSlabUid = () => { slabUidCounter += 1; return `new-${slabUidCounter}`; };

// Server slab -> editable row (all inputs kept as strings; maxItems '' = no upper limit)
function slabFromApi(s) {
  const fields = {
    minItems: String(s.minItems ?? ''),
    maxItems: s.maxItems == null ? '' : String(s.maxItems),
    fee:      String(s.fee ?? 0),
    active:   !!s.active,
  };
  return { uid: s.id, id: s.id, ...fields, orig: { ...fields }, removed: false };
}

const slabChanged = (s) =>
  !s.orig ||
  s.minItems !== s.orig.minItems ||
  s.maxItems !== s.orig.maxItems ||
  s.fee      !== s.orig.fee      ||
  s.active   !== s.orig.active;

const slabIsDirty = (s) => (s.removed ? !!s.id : slabChanged(s));

const slabPayload = (s) => ({
  minItems: Number(s.minItems),
  maxItems: s.maxItems === '' ? null : Number(s.maxItems),   // null = no upper limit
  fee:      Number(s.fee),
  active:   s.active,
});

function slabRangeLabel(s) {
  if (s.minItems === '') return 'items';
  if (s.maxItems === '') return `${s.minItems}+ items`;
  if (s.maxItems === s.minItems) return `${s.minItems} item${s.minItems === '1' ? '' : 's'}`;
  return `${s.minItems}-${s.maxItems} items`;
}

function validateSlabRow(s) {
  const min = Number(s.minItems);
  const max = Number(s.maxItems);
  const fee = Number(s.fee);
  if (s.minItems === '' || !Number.isInteger(min) || min < 1) return 'From-items must be a whole number, at least 1';
  if (s.maxItems !== '' && (!Number.isInteger(max) || max < min)) return `"To" items cannot be less than "From" (${slabRangeLabel(s)})`;
  if (s.fee === '' || Number.isNaN(fee) || fee < 0) return `Enter a valid fee for ${slabRangeLabel(s)}`;
  return null;
}

// Returns an error message (or null). Mirrors the backend overlap rule.
function validateSlabs(slabs) {
  const live = slabs.filter(s => !s.removed);
  for (const s of live) {
    const err = validateSlabRow(s);
    if (err) return err;
  }
  for (let i = 0; i < live.length; i += 1) {
    for (let j = i + 1; j < live.length; j += 1) {
      const a = live[i];
      const b = live[j];
      const aMax = a.maxItems === '' ? Infinity : Number(a.maxItems);
      const bMax = b.maxItems === '' ? Infinity : Number(b.maxItems);
      if (Number(a.minItems) <= bMax && Number(b.minItems) <= aMax) {
        return `Item ranges overlap: ${slabRangeLabel(a)} and ${slabRangeLabel(b)}`;
      }
    }
  }
  return null;
}

async function fetchSlabs() {
  const res = await api.get('/admin/settings/delivery-slabs');
  return (res.data || []).map(slabFromApi);
}

// Deletes first (frees up ranges), then updates, then creates.
async function persistSlabs(slabs) {
  const toDelete = slabs.filter(s => s.removed && s.id);
  const toUpdate = slabs.filter(s => !s.removed && s.id && slabChanged(s));
  const toCreate = slabs.filter(s => !s.removed && !s.id);

  for (const s of toDelete) {
    await api.delete(`/admin/settings/delivery-slabs/${s.id}`);
  }
  for (const s of toUpdate) {
    await api.put(`/admin/settings/delivery-slabs/${s.id}`, slabPayload(s));
  }
  for (const s of toCreate) {
    await api.post('/admin/settings/delivery-slabs', slabPayload(s));
  }
}

// ── Delivery slabs section ────────────────────────────────────────────────────
function DeliverySlabsSection({ slabs, onPatch, onRemove, onAdd }) {
  const visible = slabs.filter(s => !s.removed);
  const smallInput = { width: 74 };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>📦 Delivery fee by number of items</div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, marginBottom: 10 }}>
        If the number of different products in the cart falls in a slab, that slab's fee is used instead of the
        Delivery Fee above. Leave <strong>To</strong> empty for "no upper limit" (e.g. 15+ items). Carts that match
        no slab use the Delivery Fee above.
      </div>

      {visible.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0 10px' }}>
          No slabs yet — the Delivery Fee above applies to every cart.
        </div>
      )}

      {visible.map(s => {
        const dirty = slabChanged(s);
        const err   = validateSlabRow(s);
        return (
          <div
            key={s.uid}
            style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 10px', marginBottom: 8, borderRadius: 8, border: `1px solid ${err ? '#fca5a5' : dirty ? '#86efac' : 'var(--border)'}`, opacity: s.active ? 1 : 0.75 }}
          >
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
              From
              <input type="number" min="1" step="1" value={s.minItems} style={smallInput}
                onChange={e => onPatch(s.uid, { minItems: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
              To
              <input type="number" min="1" step="1" value={s.maxItems} placeholder="no limit" style={smallInput}
                onChange={e => onPatch(s.uid, { maxItems: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-muted)' }}>
              ₹
              <input type="number" min="0" step="0.01" value={s.fee} style={{ width: 90 }}
                onChange={e => onPatch(s.uid, { fee: e.target.value })} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, cursor: 'pointer', color: s.active ? '#15803d' : 'var(--text-muted)' }}>
              <input type="checkbox" checked={s.active} onChange={e => onPatch(s.uid, { active: e.target.checked })} />
              {s.active ? 'Active' : 'Off'}
            </label>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', flex: 1, minWidth: 70 }}>
              {slabRangeLabel(s)}{!s.id && ' · new'}
            </span>
            <button
              type="button"
              className="btn btn-ghost btn-icon btn-sm"
              title="Remove slab"
              onClick={() => onRemove(s.uid)}
              style={{ color: '#ef4444' }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}

      <button type="button" className="btn btn-ghost btn-sm" onClick={onAdd} style={{ gap: 4 }}>
        + Add slab
      </button>
    </div>
  );
}

DeliverySlabsSection.propTypes = {
  slabs: PropTypes.arrayOf(PropTypes.shape({
    uid: PropTypes.string,
    id: PropTypes.string,
    minItems: PropTypes.string,
    maxItems: PropTypes.string,
    fee: PropTypes.string,
    active: PropTypes.bool,
    removed: PropTypes.bool,
  })).isRequired,
  onPatch: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
};

function ServiceChargesModal({ onClose }) {
  const [rows,      setRows]      = useState([]);   // { key,label,description,value,active,origValue,origActive }
  const [slabs,     setSlabs]     = useState([]);   // { uid,id,minItems,maxItems,fee,active,orig,removed }
  const [freeAbove, setFreeAbove] = useState('');
  const [origFree,  setOrigFree]  = useState('');
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [chargesRes, feeRes, slabList] = await Promise.all([
          api.get('/admin/settings/charges'),
          api.get('/admin/settings/delivery-fee'),
          fetchSlabs().catch(() => null),
        ]);
        if (cancelled) return;
        const list = (chargesRes.data || [])
          .filter(c => c.key !== 'delivery_fee_multi_item')   // multi-item row removed from admin UI
          .slice()
          .sort((a, b) => CHARGE_ORDER.indexOf(a.key) - CHARGE_ORDER.indexOf(b.key))
          .map(c => ({
            key: c.key, label: c.label, description: c.description,
            value: String(c.value ?? 0), active: !!c.active,
            origValue: String(c.value ?? 0), origActive: !!c.active,
          }));
        setRows(list);
        const f = String(feeRes.data?.freeDeliveryAbove ?? '');
        setFreeAbove(f);
        setOrigFree(f);
        if (slabList) setSlabs(slabList);
        else toast('Failed to load delivery slabs', 'error');
      } catch {
        if (!cancelled) toast('Failed to load service charges', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchRow = (key, changes) =>
    setRows(rs => rs.map(r => (r.key === key ? { ...r, ...changes } : r)));

  // ── Slab edit actions ───────────────────────────────────────────────────────
  const patchSlab = (uid, changes) =>
    setSlabs(ss => ss.map(s => (s.uid === uid ? { ...s, ...changes } : s)));

  const removeSlab = (uid) =>
    setSlabs(ss => ss
      .map(s => (s.uid === uid && s.id ? { ...s, removed: true } : s))   // saved slab -> delete on Save
      .filter(s => !(s.uid === uid && !s.id)));                          // unsaved slab -> just drop

  const addSlab = () =>
    setSlabs(ss => {
      const top = ss
        .filter(s => !s.removed)
        .reduce((m, s) => Math.max(m, Number(s.maxItems !== '' ? s.maxItems : s.minItems) || 0), 0);
      return [...ss, {
        uid: newSlabUid(), id: null,
        minItems: String(top + 1), maxItems: '', fee: '', active: true,
        orig: null, removed: false,
      }];
    });

  const dirtyRows  = rows.filter(r => r.value !== r.origValue || r.active !== r.origActive);
  const freeDirty  = freeAbove !== origFree;
  const slabsDirty = slabs.some(slabIsDirty);
  const hasChanges = dirtyRows.length > 0 || freeDirty || slabsDirty;

  const handleSave = async () => {
    for (const r of dirtyRows) {
      const n = Number(r.value);
      if (r.value === '' || isNaN(n) || n < 0) { toast(`Enter a valid amount for ${r.label}`, 'error'); return; }
    }
    if (freeDirty && (freeAbove === '' || isNaN(Number(freeAbove)) || Number(freeAbove) < 0)) {
      toast('Enter a valid free-delivery amount', 'error'); return;
    }
    if (slabsDirty) {
      const slabErr = validateSlabs(slabs);
      if (slabErr) { toast(slabErr, 'error'); return; }
    }

    setSaving(true);
    try {
      await Promise.all(dirtyRows.map(r =>
        api.put(`/admin/settings/charges/${r.key}`, { value: Number(r.value), active: r.active })
      ));
      if (freeDirty) {
        await api.put('/admin/settings/delivery-fee', { freeDeliveryAbove: Number(freeAbove) });
      }
      setRows(rs => rs.map(r => ({ ...r, origValue: r.value, origActive: r.active })));
      setOrigFree(freeAbove);

      if (slabsDirty) {
        try {
          await persistSlabs(slabs);
          setSlabs(await fetchSlabs());          // reload so new slabs get their ids
        } catch (e) {
          // Some slab calls may already have gone through, so resync with the server.
          try { setSlabs(await fetchSlabs()); } catch { /* keep local state */ }
          toast(e?.response?.data?.message || 'Failed to save delivery slabs', 'error');
          return;
        }
      }
      toast('Service charges updated', 'success');
    } catch (e) {
      toast(e?.response?.data?.message || 'Failed to save charges', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truck size={16} color="#16a34a" /> Service Charges
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#15803d' }}>
          These charges are added to the customer's bill on Cart and Checkout. Turn a charge off to stop applying it.
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 13 }}>Loading…</div>
        ) : (
          <>
            {rows.map(r => {
              const dirty = r.value !== r.origValue || r.active !== r.origActive;
              return (
                <div key={r.key} style={{ border: `1px solid ${dirty ? '#86efac' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 10, opacity: r.active ? 1 : 0.75 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{CHARGE_ICONS[r.key] || '🏷️'} {r.label}</div>
                      {r.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.description}</div>}
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0, color: r.active ? '#15803d' : 'var(--text-muted)' }}>
                      <input type="checkbox" checked={r.active} onChange={e => patchRow(r.key, { active: e.target.checked })} />
                      {r.active ? 'Active' : 'Off'}
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>₹</span>
                    <input type="number" min="0" step="0.01" value={r.value}
                      onChange={e => patchRow(r.key, { value: e.target.value })}
                      style={{ width: 120 }} />
                  </div>
                </div>
              );
            })}

            <DeliverySlabsSection
              slabs={slabs}
              onPatch={patchSlab}
              onRemove={removeSlab}
              onAdd={addSlab}
            />

            <div style={{ border: `1px solid ${freeDirty ? '#86efac' : 'var(--border)'}`, borderRadius: 10, padding: '12px 14px', marginBottom: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>🎁 Free delivery above</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                When the item total reaches this amount, the delivery fee becomes ₹0 (applies to slabs too).
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>₹</span>
                <input type="number" min="0" step="1" value={freeAbove}
                  onChange={e => setFreeAbove(e.target.value)} style={{ width: 120 }} />
              </div>
            </div>
          </>
        )}

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
          <button className="btn btn-success" onClick={handleSave} disabled={saving || loading || !hasChanges} style={{ gap: 6 }}>
            <Save size={14} />{saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

ServiceChargesModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

// ── Extracted: upload a single image file ─────────────────────────────────────
// (fixes "nesting too deep" at L488/L489 — was an async callback inside map inside async fn)
async function uploadSingleImage(file) {
  // Do NOT call the backend `/uploads` endpoint. Convert the file to a data URL
  // and return that so callers can include base64 placeholders directly.
  return await new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    } catch {
      resolve(null);
    }
  });
}

// ── Extracted: replace base64 placeholders with uploaded URLs ─────────────────
function substituteUploadedUrls(previewImages, uploadedUrls) {
  let uploadIdx = 0;
  return previewImages.map(img => {
    if (img.startsWith('data:') && uploadIdx < uploadedUrls.length) {
      return uploadedUrls[uploadIdx++];
    }
    return img;
  });
}

// ── EditProductModal ──────────────────────────────────────────────────────────
function EditProductModal({ product, onClose, onSaved }) {
  const [saving,        setSaving]        = useState(false);
  const [previewImages, setPreviewImages] = useState(parseImages(product.images));
  const [newImages,     setNewImages]     = useState([]);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const [form, setForm] = useState({
    name:                   product.name || '',
    description:            product.description || '',
    price:                  product.price || '',
    category:               product.category || '',
    unit:                   product.unit || '',
    weight:                 product.weight || '',
    quantity:               product.quantity ?? '',
    ingredients:            product.ingredients || '',
    shelfLife:              product.shelfLife || '',
    madeToOrder:            product.madeToOrder || false,
    availableForBulkOrders: product.availableForBulkOrders || false,
    status:                 product.status || 'ACTIVE',
    adminRemark:            product.adminRemark || '',
    discount:               product.discount || '',
    deliveryCharges:        product.deliveryCharges || '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // ── Image handling ──────────────────────────────────────────────────────────
  const handleImageFiles = (files) => {
    const valid = Array.from(files).filter(f => f.type.startsWith('image/'));
    valid.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImages(prev => [...prev, e.target.result]);
        setNewImages(prev => [...prev, file]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (idx) => {
    const isBase64 = previewImages[idx]?.startsWith('data:');
    setPreviewImages(prev => prev.filter((_, i) => i !== idx));
    if (isBase64) {
      const base64Imgs = previewImages.filter(img => img.startsWith('data:'));
      const base64Idx  = base64Imgs.indexOf(previewImages[idx]);
      if (base64Idx !== -1) {
        setNewImages(prev => prev.filter((_, i) => i !== base64Idx));
      }
    }
  };

  // ── Upload new images, replace base64 placeholders ─────────────────────────
  // Fixed: extracted uploadSingleImage to top-level to avoid nesting > 4 deep
  const resolveUploadedImages = async () => {
    if (newImages.length === 0) return [...previewImages];

    const results     = await Promise.allSettled(newImages.map(uploadSingleImage));
    const uploadedUrls = results
      .filter(r => r.status === 'fulfilled' && r.value)
      .map(r => r.value);

    return substituteUploadedUrls(previewImages, uploadedUrls);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const finalImages = await resolveUploadedImages();
      await api.put(`/admin/products/${product.id || product.productId}`, {
        name:                   form.name,
        description:            form.description,
        price:                  form.price    !== '' ? Number(form.price)    : undefined,
        category:               form.category,
        unit:                   form.unit,
        weight:                 form.weight,
        quantity:               form.quantity !== '' ? Number(form.quantity) : undefined,
        ingredients:            form.ingredients,
        shelfLife:              form.shelfLife,
        madeToOrder:            form.madeToOrder,
        availableForBulkOrders: form.availableForBulkOrders,
        status:                 form.status,
        adminRemark:            form.adminRemark,
        discount:               form.discount         !== '' ? Number(form.discount)         : 0,
        deliveryCharges:        form.deliveryCharges  !== '' ? Number(form.deliveryCharges)  : 0,
        images:                 finalImages,
      });
      toast(`"${form.name}" updated successfully`, 'success');
      onSaved();
    } catch (err) {
      toast(err?.response?.data?.message || 'Failed to save changes', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Price preview ───────────────────────────────────────────────────────────
  const previewBase     = Number(form.price    || product.price    || 0);
  const previewCharges  = Number(product.adminCharges || 0);
  const previewDiscount = Number(form.discount || 0);
  const previewDelivery = Number(form.deliveryCharges || 0);
  const previewBefore   = previewBase + previewCharges;
  const previewCustomer = Math.max(0, previewBefore - previewDiscount);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const fieldRow = (label, children, hint) => (
    <div className="form-group" style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>{label}</label>
      {children}
      {hint && <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>{hint}</small>}
    </div>
  );

  const inp = (key, type = 'text', extra = {}) => (
    <input type={type} value={form[key]} onChange={e => set(key, e.target.value)} style={{ width: '100%' }} {...extra} />
  );

  return (
    <div className="modal-overlay" style={{ alignItems: 'flex-start', overflowY: 'auto', padding: '20px 0' }}>
      <div className="modal" style={{ maxWidth: 640, width: '100%', margin: 'auto' }}>
        <div className="modal-header">
          <span className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Edit3 size={16} /> Edit Product
          </span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Image Management */}
        <EditImageSection
          previewImages={previewImages}
          fileInputRef={fileInputRef}
          onRemove={handleRemoveImage}
          onFiles={handleImageFiles}
          buildCandidates={buildCandidates}
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          {fieldRow('Product Name *',  inp('name'),     'Changing name triggers re-approval')}
          {fieldRow('Category *',      inp('category'), 'Changing category triggers re-approval')}
          {fieldRow('Seller Price (₹) *', inp('price', 'number', { min: 0, step: 0.01 }), 'Base price set by seller')}
          {fieldRow('Unit',     inp('unit'),             'grams, kg, pieces, ml…')}
          {fieldRow('Weight',   inp('weight'),           'e.g. 250g, 500g')}
          {fieldRow('Stock Qty', inp('quantity', 'number', { min: 0 }))}
          {fieldRow('Shelf Life', inp('shelfLife'),      'e.g. 7 days, 3 months')}
          {fieldRow('Status',
            <select value={form.status} onChange={e => set('status', e.target.value)} style={{ width: '100%' }}>
              {['ACTIVE', 'INACTIVE', 'PENDING_APPROVAL', 'REJECTED'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          )}
        </div>

        {fieldRow('Description', (
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={2} style={{ width: '100%' }} placeholder="Product description..." />
        ), 'Changing description triggers re-approval')}

        {fieldRow('Ingredients (optional)', (
          <textarea value={form.ingredients} onChange={e => set('ingredients', e.target.value)} rows={2} style={{ width: '100%' }} placeholder="List ingredients if applicable..." />
        ))}

        <div style={{ display: 'flex', gap: 20, marginBottom: 16 }}>
          {[['madeToOrder', 'Made To Order'], ['availableForBulkOrders', 'Available for Bulk Orders']].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} />
              {label}
            </label>
          ))}
        </div>

        {/* Admin-Only Settings */}
        <div style={{ background: 'linear-gradient(135deg, #f0fdf4, #eff6ff)', border: '1.5px solid #bbf7d0', borderRadius: 10, padding: '14px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <AlertCircle size={14} color="#16a34a" />
            <span style={{ fontWeight: 700, fontSize: 13, color: '#15803d' }}>Admin-Only Settings</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            {fieldRow(
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Percent size={11} /> Discount Amount (₹)</span>,
              <input type="number" min="0" step="0.01" value={form.discount} onChange={e => set('discount', e.target.value)} style={{ width: '100%' }} placeholder="0" />,
              'Deducted from final price.'
            )}
            {fieldRow(
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Truck size={11} /> Delivery Charges (₹)</span>,
              <input type="number" min="0" step="0.01" value={form.deliveryCharges} onChange={e => set('deliveryCharges', e.target.value)} style={{ width: '100%' }} placeholder="0" />,
              'Shown to both seller and customer'
            )}
          </div>

          {fieldRow('Admin Remark', (
            <textarea value={form.adminRemark} onChange={e => set('adminRemark', e.target.value)} rows={2} style={{ width: '100%' }} placeholder="Internal remark for this product..." />
          ))}

          {/* Live Price Preview */}
          <PricePreview
            previewBase={previewBase}
            previewCharges={previewCharges}
            previewDiscount={previewDiscount}
            previewDelivery={previewDelivery}
            previewBefore={previewBefore}
            previewCustomer={previewCustomer}
          />
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={handleSave} disabled={saving || !form.name.trim()}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

EditProductModal.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    category: PropTypes.string,
    unit: PropTypes.string,
    weight: PropTypes.string,
    quantity: PropTypes.number,
    ingredients: PropTypes.string,
    shelfLife: PropTypes.string,
    madeToOrder: PropTypes.bool,
    availableForBulkOrders: PropTypes.bool,
    status: PropTypes.string,
    adminRemark: PropTypes.string,
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deliveryCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    images: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    adminCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ── Extracted: Edit modal image section ───────────────────────────────────────
function EditImageSection({ previewImages, fileInputRef, onRemove, onFiles, buildCandidates: bc }) {
  const handleDragOver  = (e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--primary)'; };
  const handleDragLeave = (e) => { e.currentTarget.style.borderColor = 'var(--border)'; };
  const handleDrop      = (e) => { e.preventDefault(); e.currentTarget.style.borderColor = 'var(--border)'; onFiles(e.dataTransfer.files); };

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 8, display: 'block' }}>
        Product Images
      </label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {previewImages.map((img, idx) => (
          <div key={idx} style={{ position: 'relative', width: 80, height: 80, flexShrink: 0 }}>
            <img
              src={img.startsWith('data:') ? img : (bc(img)[0] || img)}
              alt=""
              style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }}
            />
            <button
              type="button"
              onClick={() => onRemove(idx)}
              style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', border: 'none', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', padding: 0 }}
            >
              <X size={11} />
            </button>
          </div>
        ))}

        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{ width: 80, height: 80, borderRadius: 8, border: '2px dashed var(--border)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'var(--bg-hover)', gap: 4, transition: 'border-color 0.2s', flexShrink: 0 }}
        >
          <span style={{ fontSize: 24, lineHeight: 1, color: 'var(--text-muted)' }}>+</span>
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Add</span>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={e => { onFiles(e.target.files); e.target.value = ''; }}
      />
      <small style={{ color: 'var(--text-muted)', fontSize: 11 }}>
        PNG, JPG, WEBP · Click <strong>+</strong> or drag & drop to add · Click <strong>✕</strong> to remove
      </small>
    </div>
  );
}

EditImageSection.propTypes = {
  previewImages: PropTypes.arrayOf(PropTypes.string).isRequired,
  fileInputRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onFiles: PropTypes.func.isRequired,
  buildCandidates: PropTypes.func.isRequired,
};

// ── Extracted: live price preview block ───────────────────────────────────────
function PricePreview({ previewBase, previewCharges, previewDiscount, previewDelivery, previewBefore, previewCustomer }) {
  const discPct = previewDiscount > 0 && previewBefore > 0
    ? Math.round((previewDiscount / previewBefore) * 100)
    : 0;

  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', border: '1px solid #d1fae5', marginTop: 4 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 6, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
        Live Price Preview
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: 13 }}>
        <span style={{ color: 'var(--text-muted)' }}>Seller: <strong>₹{previewBase.toLocaleString('en-IN')}</strong></span>
        {previewCharges > 0 && <span style={{ color: 'var(--text-muted)' }}>+ Platform: <strong>₹{previewCharges.toLocaleString('en-IN')}</strong></span>}
        {previewDiscount > 0 && <span style={{ color: '#ef4444' }}>− Discount: <strong>₹{previewDiscount.toLocaleString('en-IN')}</strong></span>}
        <span style={{ color: 'var(--text-muted)' }}>→</span>
        {previewDiscount > 0 && <span style={{ textDecoration: 'line-through', color: '#9ca3af' }}>₹{previewBefore.toLocaleString('en-IN')}</span>}
        <span style={{ fontWeight: 800, color: '#16a34a', fontSize: 15 }}>₹{previewCustomer.toLocaleString('en-IN')}</span>
        {previewDiscount > 0 && previewBefore > 0 && (
          <span style={{ background: '#ef4444', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px' }}>
            {discPct}% OFF
          </span>
        )}
      </div>
      {previewDelivery > 0 && (
        <div style={{ fontSize: 12, color: '#2563eb', marginTop: 4 }}>
          🚚 Delivery charge: ₹{previewDelivery.toLocaleString('en-IN')}
        </div>
      )}
    </div>
  );
}

PricePreview.propTypes = {
  previewBase: PropTypes.number.isRequired,
  previewCharges: PropTypes.number.isRequired,
  previewDiscount: PropTypes.number.isRequired,
  previewDelivery: PropTypes.number.isRequired,
  previewBefore: PropTypes.number.isRequired,
  previewCustomer: PropTypes.number.isRequired,
};

// ── ProductDetailModal ────────────────────────────────────────────────────────
function ProductDetailModal({ product, onClose, onApprove, onReject, onEdit, isPending }) {
  const images = parseImages(product.images);
  const detailRows = [
    ['Category',   product.category  || '—'],
    ['Stock',      `${product.quantity ?? 0} units`],
    ['Weight',     product.weight    || '—'],
    ['Shelf Life', product.shelfLife || '—'],
    ['Rating',     product.rating    ? `⭐ ${product.rating}` : '—'],
    ['Unit',       product.unit      || '—'],
    ['Description', product.description || '—'],
    ...(product.ingredients                  ? [['Ingredients',       product.ingredients]]                                   : []),
    ...(Number(product.adminCharges)   > 0   ? [['💰 Platform Fee',   `₹${Number(product.adminCharges).toLocaleString('en-IN')}`]]  : []),
    ...(Number(product.discount)       > 0   ? [['🏷️ Discount',       `₹${Number(product.discount).toLocaleString('en-IN')} off`]]   : []),
    ...(Number(product.deliveryCharges) > 0  ? [['🚚 Delivery Charges', `₹${Number(product.deliveryCharges).toLocaleString('en-IN')}`]] : []),
    ...(product.adminRemark                  ? [['📝 Admin Remark',   product.adminRemark]]                                   : []),
    ...(product.rejectionReason              ? [['⚠️ Reject Reason',  product.rejectionReason]]                               : []),
  ];

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <span className="modal-title">Product Details</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
          <ModalImageSlider images={images} size={110} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{product.name}</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>{product.sellerBusinessName || 'Unknown Seller'}</p>
            <PriceDisplay sellerPrice={product.price} adminCharges={product.adminCharges} discount={product.discount} deliveryCharges={product.deliveryCharges} />
            <span className={`badge ${product.status?.toLowerCase()}`} style={{ marginTop: 8, display: 'inline-block' }}>{product.status}</span>
          </div>
        </div>
        {detailRows.map(([k, v]) => (
          <div className="detail-row" key={k}>
            <span className="detail-label">{k}</span>
            <span className="detail-value">{v}</span>
          </div>
        ))}
        <div className="modal-footer">
          {isPending && (
            <>
              <button className="btn btn-danger"  onClick={() => onReject(product)}><XCircle    size={14} /> Reject</button>
              <button className="btn btn-success" onClick={() => onApprove(product)}><CheckCircle size={14} /> Approve</button>
            </>
          )}
          <button className="btn btn-primary" onClick={() => onEdit(product)}><Edit3 size={14} /> Edit Product</button>
          <button className="btn btn-ghost"   onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

ProductDetailModal.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    images: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    category: PropTypes.string,
    quantity: PropTypes.number,
    weight: PropTypes.string,
    shelfLife: PropTypes.string,
    rating: PropTypes.number,
    unit: PropTypes.string,
    description: PropTypes.string,
    ingredients: PropTypes.string,
    adminCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deliveryCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    adminRemark: PropTypes.string,
    rejectionReason: PropTypes.string,
    sellerBusinessName: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    status: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  isPending: PropTypes.bool.isRequired,
};

// ── Status badge helpers ──────────────────────────────────────────────────────
const STATUS_COLORS = {
  ACTIVE:           { bg: '#dcfce7', color: '#16a34a', border: '#bbf7d0' },
  INACTIVE:         { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
  PENDING:          { bg: '#fef9c3', color: '#b45309', border: '#fde68a' },
  PENDING_APPROVAL: { bg: '#fef9c3', color: '#b45309', border: '#fde68a' },
  REJECTED:         { bg: '#fee2e2', color: '#dc2626', border: '#fecaca' },
};
const DEFAULT_STATUS_COLOR = { bg: '#f3f4f6', color: '#374151', border: '#e5e7eb' };

// ── ProductCard ───────────────────────────────────────────────────────────────
function ProductCard({ product, onViewDetails, isPending, onApprove, onReject, onEdit, approving }) {
  const stock       = product.quantity ?? 0;
  const stockClass  = stock === 0 ? 'stock-out' : stock <= 15 ? 'stock-low' : 'stock-ok';
  const images      = parseImages(product.images);
  const statusLabel = isPending ? 'PENDING' : product.status;
  const s           = STATUS_COLORS[statusLabel] || DEFAULT_STATUS_COLOR;

  return (
    <div className="product-card" style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ height: CARD_IMG_HEIGHT, minHeight: CARD_IMG_HEIGHT, flexShrink: 0, padding: '4px 4px 0', boxSizing: 'border-box' }}>
        <ImageSlider images={images} height={CARD_IMG_HEIGHT - 4} borderRadius={8} />
      </div>
      <div className="product-card-body">
        <div style={{ marginBottom: 6 }}>
          <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: '999px', fontSize: 10, fontWeight: 700, letterSpacing: '0.5px', background: s.bg, color: s.color, border: `1px solid ${s.border}` }}>
            {statusLabel}
          </span>
        </div>
        <div className="product-card-name" title={product.name}>{product.name}</div>
        <div className="product-card-seller" title={product.sellerBusinessName}>{product.sellerBusinessName || 'Unknown Seller'}</div>
        <div className="product-card-footer">
          <PriceDisplay sellerPrice={product.price} adminCharges={product.adminCharges} discount={product.discount} deliveryCharges={product.deliveryCharges} compact />
          <span className={`product-card-stock ${stockClass}`}>{stock === 0 ? '0 stock' : `${stock} stock`}</span>
        </div>
        <ProductCardActions
          isPending={isPending}
          product={product}
          approving={approving}
          onApprove={onApprove}
          onReject={onReject}
          onViewDetails={onViewDetails}
          onEdit={onEdit}
        />
      </div>
    </div>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    productId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    images: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    quantity: PropTypes.number,
    status: PropTypes.string,
    sellerBusinessName: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    adminCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    discount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deliveryCharges: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  isPending: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  approving: PropTypes.bool.isRequired,
};

// ── Extracted: card action buttons ────────────────────────────────────────────
function ProductCardActions({ isPending, product, approving, onApprove, onReject, onViewDetails, onEdit }) {
  if (isPending) {
    return (
      <div className="product-card-actions">
        <button className="btn btn-success btn-sm" onClick={() => onApprove(product)} disabled={approving}>
          <CheckCircle size={12} /> {approving ? '...' : 'Approve'}
        </button>
        <button className="btn btn-danger btn-sm" onClick={() => onReject(product)}>
          <XCircle size={12} /> Reject
        </button>
      </div>
    );
  }

  return (
    <div className="product-card-actions">
      <div style={{ display: 'flex', gap: 4, flex: 1 }}>
        <button className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => onViewDetails(product)}>
          <Eye size={12} /> View
        </button>
        <button className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: 'center' }} onClick={() => onEdit(product)}>
          <Edit3 size={12} /> Edit
        </button>
      </div>
    </div>
  );
}

ProductCardActions.propTypes = {
  isPending: PropTypes.bool.isRequired,
  product: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  approving: PropTypes.bool.isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
};

// ── Extracted: score helpers (fixes cognitive complexity > 15 in scoreProduct) ─
function scoreByName(name, query, words) {
  if (name === query)                          return 1000;
  if (name.startsWith(query))                  return 500;
  if (words.every(w => name.includes(w)))      return 400;
  return 0;
}

function scoreByCategory(cat, query) {
  if (cat === query)        return 300;
  if (cat.includes(query))  return 150;
  return 0;
}

function scoreByWordMatches(name, seller, cat, words) {
  let s = 0;
  words.forEach(w => {
    if (name.includes(w))   s += 50;
    if (seller.includes(w)) s += 30;
    if (cat.includes(w))    s += 20;
  });
  return s;
}

// ── Tiered search ─────────────────────────────────────────────────────────────
// Fixed: cognitive complexity reduced from 16 → ~8 by delegating to helpers above
function scoreProduct(p, query, words) {
  const name   = (p.name   || '').toLowerCase();
  const cat    = (p.category || '').toLowerCase();
  const seller = (p.sellerBusinessName || '').toLowerCase();

  let s = scoreByName(name, query, words)
        + scoreByCategory(cat, query)
        + scoreByWordMatches(name, seller, cat, words);

  if ((p.rating   || 0) > 0) s += p.rating * 15;
  s += (p.quantity || 0) > 0 ? 50 : -30;

  return s;
}

function tieredFilter(list, q) {
  if (!q) return list;
  const query = q.toLowerCase();
  const words = query.split(/\s+/).filter(Boolean);
  return list
    .map(p => ({ p, s: scoreProduct(p, query, words) }))
    .filter(x => x.s > 0)
    .sort((a, b) => b.s - a.s)
    .map(x => x.p);
}

// ── Fetch helpers ─────────────────────────────────────────────────────────────
async function fetchAllProductPages(statusFilter) {
  const firstRes   = await api.get('/admin/products', {
    params: { page: 0, size: 16, ...(statusFilter !== 'ALL' && { status: statusFilter }) },
  });
  const firstData  = firstRes.data;
  const totalPgs   = firstData.totalPages || 1;

  const pageRequests = [];
  for (let p = 1; p < totalPgs; p++) {
    pageRequests.push(
      api.get('/admin/products', {
        params: { page: p, size: 16, ...(statusFilter !== 'ALL' && { status: statusFilter }) },
      })
    );
  }
  const restResults = await Promise.allSettled(pageRequests);
  let allItems = [...(firstData.content || [])];
  restResults.forEach(r => {
    if (r.status === 'fulfilled') {
      allItems = [...allItems, ...(r.value.data.content || [])];
    }
  });
  return allItems;
}

// ── Main Products Page ────────────────────────────────────────────────────────
export default function Products() {
  const [activeTab,       setActiveTab]       = useState('all');
  const [products,        setProducts]        = useState([]);
  const [pending,         setPending]         = useState([]);
  const [allStats,        setAllStats]        = useState({ total: 0, active: 0, lowStock: 0, outOfStock: 0 });
  const [page,            setPage]            = useState(0);
  const [totalPages,      setTotalPages]      = useState(0);
  const [loading,         setLoading]         = useState(true);
  const [search,          setSearch]          = useState('');
  const [statusFilter,    setStatusFilter]    = useState('ALL');
  const [categoryFilter,  setCategoryFilter]  = useState('ALL');
  const [categories,      setCategories]      = useState([]);
  const [rejectTarget,    setRejectTarget]    = useState(null);
  const [approveTarget,   setApproveTarget]   = useState(null);
  const [viewTarget,      setViewTarget]      = useState(null);
  const [editTarget,      setEditTarget]      = useState(null);
  const [actionLoading,   setActionLoading]   = useState(false);
  const [processingId]    = useState(null);
  const [bulkFeeOpen,     setBulkFeeOpen]     = useState(false);
  const [chargesOpen,     setChargesOpen]     = useState(false);
  const [loadingMore,     setLoadingMore]     = useState(false);
  const sentinelRef = useRef(null);
  const toast = useToast();

  // ── Compute stats from a product list ──────────────────────────────────────
  const computeStats = (items, total) => ({
    total,
    active:     items.filter(p => p.status === 'ACTIVE').length,
    lowStock:   items.filter(p => (p.quantity ?? 0) > 0 && (p.quantity ?? 0) <= 15).length,
    outOfStock: items.filter(p => (p.quantity ?? 0) === 0).length,
  });

  // ── Load all products (category-filtered or paginated) ─────────────────────
  const loadAll = useCallback(async () => {
    const mobile = isMobile();
    // On mobile page > 0 means we're appending more items (lazy load)
    const isAppend = mobile && page > 0 && activeTab === 'all';
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      if (categoryFilter !== 'ALL') {
        const allItems = await fetchAllProductPages(statusFilter);
        const catLower = categoryFilter.toLowerCase();
        const filtered = allItems.filter(p => (p.category || '').toLowerCase() === catLower);
        setProducts(filtered);
        setTotalPages(1);
        setAllStats(computeStats(filtered, filtered.length));
      } else {
        const res   = await api.get('/admin/products', {
          params: { page, size: 16, ...(statusFilter !== 'ALL' && { status: statusFilter }) },
        });
        const data  = res.data;
        const items = data.content || [];
        if (isAppend) {
          setProducts(prev => [...prev, ...items]);
        } else {
          setProducts(items);
        }
        setTotalPages(data.totalPages || 0);
        setAllStats(computeStats(items, data.totalElements || items.length));
      }
    } catch {
      toast('Failed to load products', 'error');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, statusFilter, categoryFilter, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load categories for dropdown
  useEffect(() => {
    categoriesAPI.getAll(0, 200, 'ACTIVE')
      .then(res => {
        const data = res.data;
        setCategories(data.data || data.content || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { setPage(0); setProducts([]); }, [statusFilter, categoryFilter]);

  // ── Mobile infinite scroll via IntersectionObserver ─────────────────────────
  useEffect(() => {
    if (!isMobile() || activeTab !== 'all') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && !loadingMore && page < totalPages - 1) {
          setPage(p => p + 1);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, loadingMore, page, totalPages, activeTab]);

  const loadPending = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsAPI.getPending(0, 50);
      setPending(res.data.content || []);
    } catch {
      toast('Failed to load pending products', 'error');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'all') loadAll(); else loadPending();
  }, [activeTab, loadAll, loadPending, statusFilter]);

  // ── Actions ─────────────────────────────────────────────────────────────────
  const handleApprove = (product) => { setViewTarget(null); setApproveTarget(product); };

  const handleApproveConfirm = async (remark, charges) => {
    const product = approveTarget;
    const id      = product.id || product.productId;
    setActionLoading(true);
    try {
      await productsAPI.approve(id, remark, charges);
      toast(`"${product.name}" approved`, 'success');
      setPending(p => p.filter(x => (x.id || x.productId) !== id));
      setApproveTarget(null);
    } catch {
      toast('Failed to approve product', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectClick = (product) => { setViewTarget(null); setRejectTarget(product); };

  const handleRejectConfirm = async (reason) => {
    const product = rejectTarget;
    const id      = product.id || product.productId;
    setActionLoading(true);
    try {
      await productsAPI.reject(id, reason);
      toast(`"${product.name}" rejected`, 'info');
      setPending(p => p.filter(x => (x.id || x.productId) !== id));
      setRejectTarget(null);
    } catch {
      toast('Failed to reject product', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEdit      = (product) => { setViewTarget(null); setEditTarget(product); };
  const handleEditSaved = () => {
    setEditTarget(null);
    if (activeTab === 'all') loadAll(); else loadPending();
  };

  // ── Filter ──────────────────────────────────────────────────────────────────
  const currentList = activeTab === 'all' ? products : pending;

  const baseFiltered = activeTab === 'pending'
    ? currentList.filter(p => {
        const matchStatus   = statusFilter   === 'ALL' || p.status   === statusFilter;
        const matchCategory = categoryFilter === 'ALL' || p.category === categoryFilter;
        return matchStatus && matchCategory;
      })
    : currentList;

  const filtered = search ? tieredFilter(baseFiltered, search) : baseFiltered;

  const showPagination = activeTab === 'all' && categoryFilter === 'ALL' && totalPages > 1;

  return (
    <Layout title="Products Management" subtitle="View and manage all products">
      <div className="summary-grid">
        {[
          { label: 'Total Products', value: allStats.total,      color: 'var(--text-primary)' },
          { label: 'Active',         value: allStats.active,     color: 'var(--green)' },
          { label: 'Low Stock',      value: allStats.lowStock,   color: 'var(--yellow)' },
          { label: 'Out of Stock',   value: allStats.outOfStock, color: 'var(--red)' },
        ].map(stat => (
          <div className="summary-card" key={stat.label}>
            <div className="summary-card-value" style={{ color: stat.color }}>{stat.value}</div>
            <div className="summary-card-label">{stat.label}</div>
          </div>
        ))}
      </div>

      <div className="tabs" style={{ width: 'fit-content' }}>
        <button className={`tab ${activeTab === 'all'     ? 'active' : ''}`} onClick={() => { setActiveTab('all'); setPage(0); }}>All Products</button>
        <button className={`tab ${activeTab === 'pending' ? 'active' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending Approval {pending.length > 0 && `(${pending.length})`}
        </button>
      </div>

      <div className="filter-bar">

        {/* ── Desktop layout: single flex row (all items via display:contents wrappers) ── */}
        <div className="filter-bar-row1">
          <div className="search-box" style={{ flex: 1, minWidth: 0 }}>
            <Search size={14} className="search-icon" />
            <input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>

          <select
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setPage(0); }}
            style={{ height: 36, borderRadius: 8, border: '1px solid var(--border)', padding: '0 8px', fontSize: 13, color: 'var(--text-primary)', background: 'var(--bg-card)', cursor: 'pointer', flexShrink: 0 }}
          >
            <option value="ALL">All Categories</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>

          {/* Desktop: manage service charges (platform / delivery / rain) */}
          <button
            className="btn btn-sm set-fee-btn"
            onClick={() => setChargesOpen(true)}
            style={{ background: '#fff', color: '#15803d', border: '1px solid #16a34a', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', fontWeight: 600, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <Truck size={13} /> Service Charges
          </button>

          {/* Desktop: refresh + count */}
          <button className="btn btn-ghost btn-sm desktop-refresh" style={{ flexShrink: 0 }} onClick={() => activeTab === 'all' ? loadAll() : loadPending()}>
            <RefreshCw size={13} />
          </button>
          <span className="desktop-count" style={{ fontSize: 13, color: 'var(--text-muted)', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {filtered.length} product{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* chips row only, no count */}

        {/* ── Status chips row (desktop: display:contents; mobile: wrapped flex) ── */}
        {activeTab === 'all' && (
          <div className="filter-bar-chips-wrap">
            {['ALL','ACTIVE','PENDING_APPROVAL','REJECTED','INACTIVE'].map(s => (
              <button key={s} className={`chip ${statusFilter === s ? 'active' : ''}`} onClick={() => setStatusFilter(s)}>{s}</button>
            ))}
            {/* Mobile-only compact Charges button inside chips row */}
            <button
              className="set-fee-chip-btn mobile-only"
              onClick={() => setChargesOpen(true)}
              style={{ background: '#fff', color: '#15803d', border: '1px solid #16a34a', borderRadius: 99, alignItems: 'center', gap: 5, padding: '5px 14px', fontWeight: 600, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, display: 'none' }}
            >
              <Truck size={12} /> Charges
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="page-loader"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state" style={{ marginTop: 40 }}>
          <Package size={48} />
          <h3>{activeTab === 'pending' ? 'No pending products' : 'No products found'}</h3>
          <p>{search ? 'Try a different search term.' : 'Nothing to show here.'}</p>
        </div>
      ) : (
        <>
          <div className="product-grid">
            {filtered.map(product => (
              <ProductCard
                key={product.id || product.productId}
                product={product}
                isPending={activeTab === 'pending'}
                onViewDetails={setViewTarget}
                onApprove={handleApprove}
                onReject={handleRejectClick}
                onEdit={handleEdit}
                approving={processingId === (product.id || product.productId)}
              />
            ))}
          </div>

          {/* Mobile lazy-load sentinel */}
          {activeTab === 'all' && (
            <div ref={sentinelRef} className="lazy-sentinel">
              {loadingMore && <div className="spinner" />}
            </div>
          )}

          {showPagination && (
            <div className="pagination" style={{ marginTop: 24 }}>
              <span>Page {page + 1} of {totalPages}</span>
              <div className="pagination-controls">
                <button className="btn btn-ghost btn-sm" disabled={page === 0}              onClick={() => setPage(p => p - 1)}>← Prev</button>
                <button className="btn btn-ghost btn-sm" disabled={page >= totalPages - 1}  onClick={() => setPage(p => p + 1)}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}

      {bulkFeeOpen   && <BulkPlatformFeeModal onClose={() => setBulkFeeOpen(false)} onApplied={loadAll} />}
      {chargesOpen   && <ServiceChargesModal onClose={() => setChargesOpen(false)} />}
      {rejectTarget  && <RejectModal  product={rejectTarget}  onConfirm={handleRejectConfirm}  onClose={() => setRejectTarget(null)}  loading={actionLoading} />}
      {approveTarget && <ApproveModal product={approveTarget} onConfirm={handleApproveConfirm} onClose={() => setApproveTarget(null)} loading={actionLoading} />}
      {viewTarget && !editTarget && (
        <ProductDetailModal product={viewTarget} onClose={() => setViewTarget(null)} onApprove={handleApprove} onReject={handleRejectClick} onEdit={handleEdit} isPending={activeTab === 'pending'} />
      )}
      {editTarget && <EditProductModal product={editTarget} onClose={() => setEditTarget(null)} onSaved={handleEditSaved} />}
    </Layout>
  );
}